import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { PageHeader } from '../../layouts/Shells';
import type { ReactNode } from 'react';
import {
  Badge,
  Button,
  Dialog,
  Drawer,
  EmptyState,
  InlineError,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import type { Json } from '../../types/database.types';
import { NewClientLinkDialog } from './NewClientLinkDialog';
import {
  useAcknowledgeSupplierRevision,
  useCgAccess,
  useCgItems,
  useCgOrderCosts,
  useCgOrders,
  useCgProductTypes,
  useCgStatuses,
  useCgTemplates,
  useCreateCgOrder,
  useDeleteCgOrder,
  useOrderApproval,
  useUploadRevisedProof,
  useDeleteCgTemplate,
  useSaveCgTemplate,
  useShipTargetDays,
  useGateState,
  useGateTasks,
  useOrderReviewToken,
  useOverrideFinancialGate,
  useClientApproved,
  useOrderActivity,
  usePoDocuments,
  useCertificate,
  useOrderSupplier,
  useReissueReviewToken,
  useRevokeReviewToken,
  useSendPoToSupplier,
  useUpdateCgOrder,
  useUpdateShipTarget,
} from './hooks';
import type { ActiveReviewToken, CgItemInput, CgOrderInput, CgOrderUpdate } from './queries';
import {
  attentionOrders,
  draftOrders,
  cgErrorMessage,
  daysInProduction,
  daysToAcknowledge,
  daysToShip,
  isOpenStatus,
  isArchivedStatus,
  monthlySales,
  orderRevenue,
  orderTotal,
  searchOrders,
  todayIso,
} from './types';
import type { CgOrder, CgOrderItem, CgOrderType } from './types';
import { costsByOrder, orderProfit, profitSummary, type CgOrderCost } from './orderProfit';
import { ProfitReport } from './ProfitReport';
import { detectCarrier } from './tracking';
import { matchProductType } from './orderPrefill';
import { slaRemaining, slaTone, todayNow } from './sla';
import { FinancialGatePanel } from './FinancialGatePanel';
import { blockedStatuses, forwardOnlyReason } from './statusFlow';
import {
  APPROVAL_STATUS,
  FINANCIAL_GATE_MESSAGE,
  GATED_STATUS,
  blockedByApproval,
  blockedByGate,
  gateBlocks,
  outstandingTasks,
  overrideProblem,
  sendPoProblem,
} from './financialGate';
import { ownerHint, resolveOwner } from './orderOwner';
import { revisionOutstanding, revisionSummary } from './supplierRevision';
import { DEFAULT_REVIEW_LINK_DAYS, linkLife, shouldReissue } from './reviewLink';
import { describeActivity, sortActivity } from './orderActivity';
import { PoHistory } from './PoHistory';
import { CertificateLink } from './CertificateLink';
import { SupplierScorecard } from './SupplierScorecard';
import { InventorySupplierPicker } from '../supplierExchange/SupplierExchangePage';
import { revisionNotice, reviewUrl } from './revisionNotice';
import { useTapPeople } from '../projects/hooks';
import { useAuth } from '../auth/AuthProvider';
import { useWorkspaceSettings } from '../settings/hooks';

/**
 * The fallback while workspace_settings has not loaded, and if it ever holds
 * nothing. Mirrors the cg_orders.sample_due_days default set in
 * 20260926000100 — the workspace value is what an admin sets, this is what the
 * database falls back on, and they should not disagree.
 */
const DEFAULT_SAMPLE_DUE_DAYS = 21;
import type { OrderPrefill } from './orderPrefill';
import { useComponentTypes } from '../supplierExchange/sampleReview';
import { approvalSummary, componentRows, statusLabel, statusTone } from './approvalPanel';
import { artworkComponents } from '../artwork/componentVocabulary';
import { CgPipeline } from './CgPipeline';
import {
  SCOPE_LABELS,
  filterOrdersByScope,
  orderTypeLabel,
  resolveScope,
  scopesForTab,
  statusLabelOf,
  statusesForOrder,
  hasSupplierStage,
  scopeFileTag,
} from './pipelineScope';
import type { CgPipelineScope } from './pipelineScope';
import { useSetHandoffPo } from '../supplierExchange/hooks';

type CgTab = 'dashboard' | 'pipeline' | 'orders' | 'inventory' | 'reports';

const TABS: { id: CgTab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  // Between the summary and the table on purpose: it answers "where is
  // everything", which is the question asked before "show me the rows".
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'orders', label: 'Orders' },
  // Beside the orders it is about, rather than under a "Supplier Reviews" tab
  // filed by system. What a factory is holding is a question you ask WHILE
  // looking at orders, not on a screen you navigate to separately.
  { id: 'inventory', label: 'Inventory' },
  { id: 'reports', label: 'Reports' },
];

function money(value: number | null): string {
  if (value === null) {
    return '—';
  }
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function CgOrdersPage() {
  const access = useCgAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<CgTab>('dashboard');
  /**
   * Which pipeline the whole page is looking at.
   *
   * Held here rather than in each tab, so the Dashboard's numbers and the
   * board's columns can never describe different pipelines at the same time.
   * `resolveScope` is what the tabs read: picking All on Orders and clicking
   * Dashboard moves the control back to Custom in the same render as the data,
   * rather than leaving an "All" label over custom-only numbers.
   */
  const [scope, setScope] = useState<CgPipelineScope>('custom');
  const activeScope = resolveScope(scope, tab);
  // Owned by the page, not by the panel: the panel unmounts when you leave the
  // tab, and state held inside it would reset the chosen supplier every time
  // somebody glanced at another tab.
  const [inventorySupplier, setInventorySupplier] = useState('');
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [clientLinkOpen, setClientLinkOpen] = useState(false);

  // Arriving from an Inbox notification: /cg-orders?order=<id>.
  // notificationHref has always produced this link and the page has never read
  // it, so every CG alert landed on the dashboard with the order it was about
  // still unopened.
  //
  // Sends them to the job page rather than opening the drawer. An alert is
  // about the whole job — a sample came back, a supplier asked a question —
  // and the drawer is an edit form for three fields. Replaces the history
  // entry so Back returns to wherever they came from rather than to a link
  // that immediately redirects again.
  const orderParam = new URLSearchParams(location.search).get('order');
  useEffect(() => {
    if (orderParam === null || orderParam === '') {
      return;
    }
    void navigate(`/cg-orders/${orderParam}`, { replace: true, state: null });
  }, [orderParam, navigate]);

  // Arriving from "Issue PO & Create CG Order". The state is read once and
  // then cleared from history, so a refresh or a back-navigation does not
  // reopen a half-filled order the person already dealt with.
  const prefill = (location.state as { prefill?: OrderPrefill } | null)?.prefill ?? null;
  useEffect(() => {
    if (prefill === null) {
      return;
    }
    setNewOrderOpen(true);
    void navigate(location.pathname, { replace: true, state: null });
  }, [prefill, navigate, location.pathname]);

  if (access.isLoading) {
    return <Skeleton lines={6} />;
  }
  if (access.data !== true) {
    return (
      <EmptyState
        title="CG Orders is limited to the CG team"
        description="Ask Kyu to add you to the CG members list if you need access."
      />
    );
  }
  return (
    <div>
      {/* CG Orders opened straight into its tabs — the one module in the app
          with no name anywhere on screen. It takes one on the same terms as
          every other dense route: in the top bar, no description. */}
      <PageHeader title="CG Orders" topbar />
      <div className="cg-page-header">
        <nav className="tracker-tab-nav" aria-label="CG Orders sections">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`tracker-tab-button${entry.id === tab ? ' tracker-tab-button--active' : ''}`}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </nav>
        <div className="cg-pipeline-switch" role="group" aria-label="Pipeline">
          {scopesForTab(tab).map((entry) => (
            <button
              key={entry}
              type="button"
              className={`cg-pipeline-switch-button${
                entry === activeScope ? ' cg-pipeline-switch-button--active' : ''
              }`}
              aria-pressed={entry === activeScope}
              onClick={() => setScope(entry)}
            >
              {SCOPE_LABELS[entry]}
            </button>
          ))}
        </div>
        <div className="cg-page-actions">
          {/* Lives on the page header rather than under Jobs & Approvals
              because that sub-tab does not exist yet (9d). Moving it is a
              one-line change; leaving the wizard unreachable until then is
              not. */}
          <Button size="sm" variant="secondary" onClick={() => setClientLinkOpen(true)}>
            + New Client Link
          </Button>
          <Button size="sm" onClick={() => setNewOrderOpen(true)}>
            New Order
          </Button>
        </div>
      </div>
      {tab === 'dashboard' ? <CgDashboard scope={activeScope} /> : null}
      {/* The board opens the job page, the same as a row in the table. Kyu:
          "from the pipeline, the details I needed only pops up in the same
          small tight dialog box — I need this to open the order page." The
          board handed an id to the Orders view and asked it to open the
          drawer, which made the card the one place in the app where clicking a
          job got you an edit form instead of the job. */}
      {tab === 'pipeline' ? (
        <CgPipeline
          scope={activeScope}
          onOpenOrder={(order) => void navigate(`/cg-orders/${order.id}`)}
        />
      ) : null}
      {tab === 'orders' ? <CgOrdersView scope={activeScope} /> : null}
      {tab === 'inventory' ? (
        <InventorySupplierPicker
          supplierId={inventorySupplier}
          onSupplierChange={setInventorySupplier}
        />
      ) : null}
      {tab === 'reports' ? <CgReports scope={activeScope} /> : null}
      <Drawer open={newOrderOpen} title="New Order" onClose={() => setNewOrderOpen(false)}>
        <CgNewOrder
          prefill={prefill}
          onCreated={() => {
            setNewOrderOpen(false);
            setTab('orders');
          }}
        />
      </Drawer>
      <NewClientLinkDialog open={clientLinkOpen} onClose={() => setClientLinkOpen(false)} />
    </div>
  );
}

function useOrderData() {
  const orders = useCgOrders();
  const items = useCgItems();
  const itemsByOrder = useMemo(() => {
    const map = new Map<string, CgOrderItem[]>();
    for (const item of items.data ?? []) {
      const list = map.get(item.order_id);
      if (list === undefined) {
        map.set(item.order_id, [item]);
      } else {
        list.push(item);
      }
    }
    return map;
  }, [items.data]);
  // The supplier bills (20270162000100), grouped the same way. A failed read
  // leaves every order on its older cost or "Pending bill"; it never blocks
  // the list.
  const costs = useCgOrderCosts();
  const billsByOrder = useMemo(() => costsByOrder(costs.data ?? []), [costs.data]);
  return { orders, items, itemsByOrder, costs, billsByOrder };
}

function CgDashboard({ scope }: { scope: CgPipelineScope }) {
  const { orders: allOrdersQuery, itemsByOrder } = useOrderData();
  const orders = allOrdersQuery;
  const statuses = useCgStatuses(scope);
  const target = useShipTargetDays();
  const updateTarget = useUpdateShipTarget();
  const [editingTarget, setEditingTarget] = useState('');
  const [slaOpen, setSlaOpen] = useState(false);
  const today = todayIso();

  if (orders.isLoading || statuses.isLoading) {
    return <Skeleton lines={6} />;
  }
  // Every number below is drawn from ONE pipeline, which is the whole reason
  // the switcher scopes the page. Kyu: "I do not want blended numbers that look
  // precise but are not useful."
  const allOrders = filterOrdersByScope(orders.data ?? [], scope);
  const allStatuses = statuses.data ?? [];
  const targetDays = target.data ?? 21;

  const month = today.slice(0, 7);
  const paidThisMonth = allOrders.filter((order) => order.date_paid?.startsWith(month));
  const revenueThisMonth = paidThisMonth.reduce(
    (sum, order) => sum + orderTotal(order, itemsByOrder.get(order.id) ?? []),
    0,
  );
  const open = allOrders.filter(
    (order) => isOpenStatus(order.status, allStatuses) && order.date_shipped === null,
  );
  const openValue = open.reduce(
    (sum, order) => sum + orderTotal(order, itemsByOrder.get(order.id) ?? []),
    0,
  );
  const shipDays = allOrders
    .map((order) => daysToShip(order))
    .filter((value): value is number => value !== null);
  const avgShip =
    shipDays.length === 0 ? null : shipDays.reduce((a, b) => a + b, 0) / shipDays.length;
  const ackDays = allOrders
    .map((order) => daysToAcknowledge(order))
    .filter((value): value is number => value !== null);
  const avgAck = ackDays.length === 0 ? null : ackDays.reduce((a, b) => a + b, 0) / ackDays.length;
  const prodDays = allOrders
    .map((order) => daysInProduction(order))
    .filter((value): value is number => value !== null);
  const avgProd =
    prodDays.length === 0 ? null : prodDays.reduce((a, b) => a + b, 0) / prodDays.length;
  const months = monthlySales(allOrders, itemsByOrder, 6, today);
  const peak = Math.max(1, ...months.map((point) => point.total));
  const attention = attentionOrders(allOrders, allStatuses, targetDays, today);
  const drafts = draftOrders(allOrders);

  return (
    <div>
      <div className="tracker-kpi-grid">
        <Kpi
          label="Revenue · this month"
          value={money(revenueThisMonth)}
          sub={`${paidThisMonth.length} orders`}
        />
        <Kpi label="Open orders" value={String(open.length)} sub="not yet shipped" />
        <div className="tracker-kpi-card">
          <div className="tracker-kpi-label">Avg days to ship</div>
          <div className="tracker-kpi-value">{avgShip === null ? '—' : avgShip.toFixed(1)}</div>
          <div className="tracker-kpi-sub">
            <button
              type="button"
              className="cg-sla-trigger"
              aria-expanded={slaOpen}
              onClick={() => setSlaOpen((prev) => !prev)}
            >
              SLA details {attention.length > 0 ? `· ${attention.length} over target` : ''}
            </button>
          </div>
          {slaOpen ? (
            <div className="filter-popover cg-sla-popover">
              <p className="cg-sla-line">
                <span>PO sent → acknowledged</span>
                <strong>{avgAck === null ? '—' : `${avgAck.toFixed(1)}d`}</strong>
              </p>
              <p className="cg-sla-line">
                <span>Acknowledged → shipped</span>
                <strong>{avgProd === null ? '—' : `${avgProd.toFixed(1)}d`}</strong>
              </p>
              <p className="cg-sla-line">
                <span>Over the {targetDays}-day target</span>
                <strong>{attention.length}</strong>
              </p>
              <div className="cg-target-edit">
                <Input
                  label="New target"
                  labelHidden
                  value={editingTarget}
                  onChange={(event) => setEditingTarget(event.target.value)}
                  placeholder={String(targetDays)}
                  aria-label="New ship target in days"
                />
                <Button
                  variant="secondary"
                  onClick={() => {
                    const parsed = Number(editingTarget);
                    if (Number.isFinite(parsed) && parsed > 0) {
                      updateTarget.mutate(Math.round(parsed));
                      setEditingTarget('');
                    }
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        <Kpi label="$ in production" value={money(openValue)} sub="open order value" />
      </div>
      <section className="tracker-dashboard-card">
        <h2 className="tracker-dashboard-card-title">Monthly sales (last 6 months)</h2>
        <div className="tracker-funnel">
          {months.map((point) => (
            <div key={point.month} className="tracker-funnel-row">
              <span className="tracker-funnel-label">{point.label}</span>
              <span className="tracker-funnel-track">
                <span
                  className="tracker-funnel-fill"
                  style={{
                    width: `${point.total === 0 ? 0 : Math.max(2, (point.total / peak) * 100)}%`,
                  }}
                />
              </span>
              <span className="cg-funnel-count">
                {money(point.total)} · {point.orders} orders
              </span>
            </div>
          ))}
        </div>
      </section>
      {drafts.length === 0 ? null : (
        <section className="tracker-dashboard-card">
          <h2 className="tracker-dashboard-card-title">Waiting on details</h2>
          <p className="cg-muted">
            {drafts.length === 1
              ? '1 order was opened from an approved sample and still needs its client and amounts.'
              : `${drafts.length} orders were opened from approved samples and still need their client and amounts.`}
          </p>
          <ul className="cg-attention-list">
            {drafts.map((order) => (
              <li key={order.id}>
                <strong>{order.order_no}</strong> — {order.notes === '' ? 'no notes' : order.notes}
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="tracker-dashboard-card">
        <h2 className="tracker-dashboard-card-title">Attention required</h2>
        {attention.length === 0 ? (
          <p className="cg-muted">Nothing past the {targetDays}-day target. All clear.</p>
        ) : (
          <ul className="cg-attention-list">
            {attention.map((entry) => (
              <li key={entry.order.id}>
                <strong>
                  {entry.order.order_no} {entry.order.client}
                </strong>{' '}
                — {entry.days} days since PO sent (target {targetDays})
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Kpi(props: { label: string; value: string; sub: ReactNode }) {
  return (
    <div className="tracker-kpi-card">
      <div className="tracker-kpi-label">{props.label}</div>
      <div className="tracker-kpi-value">{props.value}</div>
      <div className="tracker-kpi-sub">{props.sub}</div>
    </div>
  );
}

function CgOrdersView({ scope }: { scope: CgPipelineScope }) {
  const { orders, items, itemsByOrder, billsByOrder } = useOrderData();
  const statuses = useCgStatuses(scope);
  const update = useUpdateCgOrder();
  const remove = useDeleteCgOrder();
  const openOrder = useNavigate();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CgOrder | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<CgOrder | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [error, setError] = useState('');
  const today = todayIso();

  if (orders.isLoading || items.isLoading || statuses.isLoading) {
    return <Skeleton lines={8} />;
  }
  const visible = searchOrders(filterOrdersByScope(orders.data ?? [], scope), search);
  const allStatuses = statuses.data ?? [];
  // Kyu: "when All is selected, make sure the order type is clearly visible in
  // the table so Custom and In-Stock don't blur together." Only under All —
  // a column reading "Custom" on every row of a Custom-only view is noise.
  const showType = scope === 'all';

  const runUpdate = (orderId: string, patch: CgOrderUpdate) => {
    setError('');
    update.mutate(
      { orderId, update: patch },
      { onError: (err) => setError(cgErrorMessage(err.message)) },
    );
  };

  return (
    <div>
      <div className="cg-toolbar">
        <Input
          label="Search orders"
          labelHidden
          placeholder="Search orders…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {visible.length === 0 ? (
        <EmptyState
          title="No orders"
          description="Create the first order from the New Order tab."
        />
      ) : (
        <table className="tracker-table">
          <thead>
            <tr>
              <th>Order</th>
              {showType ? <th>Pipeline</th> : null}
              <th>Client</th>
              <th>Status</th>
              {/* Kyu, 22 Sep: "we can see the revenue and then the bill and
                  then the profit and the margin in one line per row". */}
              <th>Revenue</th>
              <th>Bill</th>
              <th>Profit</th>
              <th>Margin</th>
              <th>Days to ship</th>
              <th>Sample SLA</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((order) => {
              const orderItems = itemsByOrder.get(order.id) ?? [];
              const money$ = orderProfit(order, orderItems, billsByOrder.get(order.id) ?? []);
              const ship = daysToShip(order);
              // ── The row opens the CASE FILE, not the edit drawer ────────
              //
              // The page existed for a while and almost nobody found it: the
              // only way in was a ghost link in the drawer's FOOTER, below
              // line items, ship-to and two notes fields. The replacement was
              // reachable only from the bottom of the thing it replaced.
              //
              // So the default is inverted. Clicking a job opens the whole
              // job; editing three fields is now an explicit choice in the
              // actions column, which is the rarer act.
              return (
                <tr
                  key={order.id}
                  className="cg-order-row"
                  onClick={() => void openOrder(`/cg-orders/${order.id}`)}
                >
                  <td>
                    {/* A real link, so it behaves like one: cmd-click opens a
                        second job in a new tab, which is most of how anybody
                        compares two of them. */}
                    <Link
                      className="cg-order-no"
                      to={`/cg-orders/${order.id}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {order.order_no}
                    </Link>
                    {/* On the order number rather than in the status column,
                        because it is not a status — the order is still in
                        whatever stage it was. It is a note that the stage
                        cannot advance until somebody answers the factory. */}
                    {revisionOutstanding(order) ? (
                      <Badge tone="danger">Supplier blocked</Badge>
                    ) : null}
                    <div className="cg-muted">{order.region}</div>
                  </td>
                  {showType ? (
                    <td>
                      {/* Named rather than colour-coded: a tone on its own
                          would leave somebody guessing which of the two it
                          meant, which is exactly the blurring this column is
                          here to prevent. */}
                      <Badge tone={order.order_type === 'in_stock' ? 'brand' : 'neutral'}>
                        {orderTypeLabel(order)}
                      </Badge>
                    </td>
                  ) : null}
                  <td>
                    {order.client === '' ? (
                      <Badge tone="warning">Needs details</Badge>
                    ) : (
                      order.client
                    )}
                  </td>
                  <td onClick={(event) => event.stopPropagation()}>
                    {/* Earlier statuses are rendered but disabled once an
                        order is being built, so the rule is visible rather
                        than the list silently changing shape. Browser-side
                        only — the database still permits any transition. */}
                    <Select
                      label={`Status for ${order.order_no}`}
                      labelHidden
                      title={forwardOnlyReason(order, allStatuses)}
                      value={order.status}
                      onChange={(event) => runUpdate(order.id, { status: event.target.value })}
                      // The row's OWN ladder. Under All the list holds
                      // both, and offering an In-Stock stage on a custom order
                      // would be refused by the database on click — a dropdown
                      // whose options bounce is worse than one that never
                      // offered them.
                      options={statusesForOrder(order, allStatuses).map((status) => ({
                        value: status.id,
                        label: statusLabelOf(status),
                        disabled: blockedStatuses(order, allStatuses).has(status.id),
                      }))}
                    />
                  </td>
                  <td>{money(money$.revenue)}</td>
                  <td>{money$.bill === null ? '—' : money(money$.bill)}</td>
                  <td>
                    {money$.profit === null ? (
                      <Badge tone="warning">Pending bill</Badge>
                    ) : (
                      money(money$.profit)
                    )}
                  </td>
                  <td>{money$.marginPct === null ? '—' : `${money$.marginPct.toFixed(1)}%`}</td>
                  <td>{ship === null ? '—' : `${ship}d`}</td>
                  <td>
                    {/* The same lens the supplier portal renders, so both
                        sides of the relationship are looking at one clock
                        rather than two that can disagree. */}
                    {order.sample_clock_started_at === null ? (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    ) : (
                      <Badge
                        tone={slaTone(
                          slaRemaining(
                            order.sample_clock_started_at,
                            order.sample_due_days,
                            todayNow(),
                            order.production_clock_started_at,
                          ).state,
                        )}
                      >
                        {
                          slaRemaining(
                            order.sample_clock_started_at,
                            order.sample_due_days,
                            todayNow(),
                            // Stops the clock at sample approval. Without this
                            // an In Production order shows a sample badge that
                            // is still counting; see sla.ts.
                            order.production_clock_started_at,
                          ).label
                        }
                      </Badge>
                    )}
                  </td>
                  <td className="cg-row-actions" onClick={(event) => event.stopPropagation()}>
                    {/* The flex box is the DIV, never the cell. A <td> given
                        display: flex stops being a table cell: it leaves the
                        row's height and the column grid, its stripe fill no
                        longer spans the cell, and two wrapped buttons centre
                        themselves over the row's fixed height and overlap.
                        Seen live 17 September: "Mark shipped" printed on top
                        of "Quick edit". tableCells.test.ts guards this. */}
                    <div className="cg-row-actions-group">
                      {/* The drawer, now that it is not the default. Named for
                        what it is good at rather than "Edit", which would
                        imply the page is read-only — it is not. */}
                      <Button variant="ghost" onClick={() => setEditing(order)}>
                        Quick edit
                      </Button>
                      {order.date_acknowledged === null && order.date_po_sent !== null ? (
                        <Button
                          variant="secondary"
                          onClick={() => runUpdate(order.id, { dateAcknowledged: today })}
                        >
                          Mark acked
                        </Button>
                      ) : null}
                      {order.date_shipped === null ? (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            runUpdate(order.id, { dateShipped: today, status: 'Shipped' })
                          }
                        >
                          Mark shipped
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {editing === null ? null : (
        <CgOrderDialog
          title={`Edit ${editing.order_no}`}
          initial={editing}
          initialItems={itemsByOrder.get(editing.id) ?? []}
          onClose={() => setEditing(null)}
          onRequestDelete={() => setConfirmingDelete(editing)}
        />
      )}
      <Dialog
        open={confirmingDelete !== null}
        title="Delete order?"
        onClose={() => setConfirmingDelete(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (confirmingDelete !== null) {
                  setError('');
                  // A delete can be refused — a signed-off order is kept by
                  // its certificate. Without an onError the refusal was
                  // swallowed and the order stayed on screen with no reason
                  // given, which is indistinguishable from a dead button.
                  remove.mutate(
                    { orderId: confirmingDelete.id, reason: deleteReason },
                    { onError: (err) => setError(cgErrorMessage(err.message)) },
                  );
                }
                setConfirmingDelete(null);
                setDeleteReason('');
                setEditing(null);
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        <p>
          This removes {confirmingDelete?.order_no} ({confirmingDelete?.client}) and its line items.
          The activity log keeps its history.
        </p>
        {/* Offered for every delete and required by the database only for an
            order a client signed off. Asking here rather than only after a
            refusal means the reason is written while somebody still remembers
            it, instead of being retyped to get past an error. */}
        <Textarea
          label="Reason (required if the client signed this order off)"
          value={deleteReason}
          onChange={(event) => setDeleteReason(event.target.value)}
          placeholder="e.g. test order from an end-to-end rehearsal"
          rows={2}
        />
      </Dialog>
    </div>
  );
}

/**
 * Client approval, per component, with the way back in.
 *
 * Two jobs. It shows the state of all four proofs — including the client's own
 * words when they sent one back — and it is the only place in the app that can
 * upload a replacement. Those belong together: a re-upload control with no
 * sight of the note is a button somebody presses without knowing what to fix.
 *
 * Existing assets supply their own component identities, so an old order
 * keeps its truthful labels after those catalogue rows become inactive. Only
 * an open order with no assets reads the active catalogue for empty slots. A
 * closed assetless order has no artwork history and says so instead of drawing
 * four invented placeholders.
 */
function ClientApprovalPanel({ orderId, closed }: { orderId: string; closed: boolean }) {
  const approval = useOrderApproval(orderId);
  const componentTypes = useComponentTypes();
  const upload = useUploadRevisedProof();
  const [error, setError] = useState('');
  const [busyComponent, setBusyComponent] = useState('');

  if (approval.isPending || componentTypes.isPending) {
    return <Skeleton lines={3} label="Loading client approval" />;
  }
  if (approval.isError) {
    return <InlineError>Client approval could not be loaded.</InlineError>;
  }

  const assets = approval.data?.assets ?? [];
  if (assets.length === 0 && closed) {
    return (
      <div className="cg-approval">
        <div className="cg-approval-head">
          <h3 className="cg-approval-title">Client approval</h3>
        </div>
        <p>No artwork on this order.</p>
      </div>
    );
  }

  const components =
    assets.length > 0
      ? artworkComponents(assets.map((asset) => asset.component_type))
      : (componentTypes.data ?? []).map((entry) => entry.id);
  const rows = componentRows(components, assets, approval.data?.decisions ?? []);

  // No proofs and no components is an order that never went through the client
  // link. Saying so beats an empty box that looks broken.
  if (rows.length === 0) {
    return null;
  }

  function send(component: string, file: File) {
    setError('');
    setBusyComponent(component);
    upload.mutate(
      { orderId, component, file },
      {
        onError: (err) => setError(cgErrorMessage(err.message)),
        onSettled: () => setBusyComponent(''),
      },
    );
  }

  return (
    <div className="cg-approval">
      <div className="cg-approval-head">
        <h3 className="cg-approval-title">Client approval</h3>
        <span className="cg-approval-summary">{approvalSummary(rows)}</span>
      </div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <ul className="cg-approval-rows">
        {rows.map((row) => (
          <li key={row.component} className="cg-approval-row">
            <div className="cg-approval-row-head">
              <span className="cg-approval-component">{row.component}</span>
              <Badge tone={statusTone(row.status)} micro>
                {statusLabel(row.status)}
              </Badge>
              {row.version === 0 ? null : (
                <span className="cg-approval-version">v{row.version}</span>
              )}
              {!row.canReupload ? null : (
                <label className="ui-button ui-button--secondary ui-button--sm cg-approval-upload">
                  {busyComponent === row.component ? 'Uploading…' : 'Upload new version'}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    aria-label={`Upload a new version of ${row.component}`}
                    disabled={upload.isPending}
                    style={{ display: 'none' }}
                    onChange={(event) => {
                      const chosen = event.target.files?.[0];
                      event.target.value = '';
                      if (chosen === undefined) {
                        return;
                      }
                      send(row.component, chosen);
                    }}
                  />
                </label>
              )}
            </div>
            {row.note === '' ? null : (
              <p className="cg-approval-note">
                <span className="cg-approval-note-label">Client asked for:</span> {row.note}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ItemDraft {
  productType: string;
  description: string;
  units: string;
  unitPrice: string;
  unitCost: string;
  /** Both are printed per line on the Production PO. */
  sku: string;
  spec: string;
}

/**
 * What has actually happened to this order.
 *
 * cg_activity has fourteen writers and, until now, no readers. The delete
 * dialog has always said "The activity log keeps its history" — copy about a
 * log nobody in the product could open. Every "when did…" and "who…" question
 * about a CG order has been answerable only with database access.
 *
 * Read-only by construction, and not merely by omission: cg_activity is
 * append-only at the database (cg_activity_guard), and authenticated holds no
 * UPDATE or DELETE grant. So this is a view of what happened, not of what the
 * app remembered to record — which is the only kind of history worth showing.
 *
 * Names resolved from the people the drawer already loaded rather than by
 * joining: profiles is a separate read and the owner picker has the list in
 * hand. An unresolvable actor prints nothing rather than a uuid — a client
 * sign-off and a trigger both legitimately have no Hub account behind them.
 */
function OrderActivityFeed({
  orderId,
  people,
}: {
  orderId: string;
  people: { id: string; label: string }[];
}) {
  const activity = useOrderActivity(orderId);

  if (activity.isPending) {
    return <Skeleton lines={3} label="Loading history" />;
  }
  if (activity.isError) {
    return <InlineError>This order&rsquo;s history could not be loaded.</InlineError>;
  }

  const lines = sortActivity((activity.data ?? []).map(describeActivity));
  if (lines.length === 0) {
    return null;
  }
  const nameOf = new Map(people.map((person) => [person.id, person.label]));

  return (
    <div className="cg-activity">
      <p className="cg-activity-title">History</p>
      <ul className="cg-activity-list">
        {lines.map((line) => {
          const who = nameOf.get(line.actorId) ?? '';
          return (
            <li key={line.id} className={`cg-activity-row is-${line.tone}`}>
              <span className="cg-activity-when">{line.createdAt.slice(0, 10)}</span>
              <span className="cg-activity-what">
                {who === '' ? null : <strong>{who} </strong>}
                {line.sentence}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The client's review link: its state, and the two things you can do to it.
 *
 * Before 20261023000100 neither action existed. A link that had expired — the
 * default life is 30 days, and clients go quiet over holidays — could only be
 * replaced by running the client-link wizard again, which creates a SECOND
 * ORDER. Two orders for one job, both counted in every revenue and margin
 * report. The portal's own message, "ask your contact at TAP for a fresh
 * link", led followed literally to corrupted numbers.
 *
 * Revoking had no workaround at all: a link sent to the wrong contact stayed
 * live and login-free until it lapsed, unless somebody deleted the order.
 *
 * The panel states the remaining life before offering Copy, because "live" is
 * not the useful fact — two days left and three weeks left are both live, and
 * only one of them is safe to send to somebody about to go on holiday.
 */
export function ReviewLinkPanel({
  orderId,
  live,
  onError,
}: {
  orderId: string;
  live: ActiveReviewToken | null;
  onError: (message: string) => void;
}) {
  const reissue = useReissueReviewToken();
  const revoke = useRevokeReviewToken();
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');

  const life = live === null ? null : linkLife(live.expiresAt, new Date());
  const url = live === null ? '' : reviewUrl(window.location.origin, live.token);

  return (
    <div className="cg-review-link">
      <div className="cg-review-link-head">
        <strong>Client review link</strong>
        {life === null ? (
          <Badge tone="neutral">None live</Badge>
        ) : (
          <Badge tone={life.tone === 'success' ? 'success' : life.tone}>{life.label}</Badge>
        )}
      </div>

      {live === null ? (
        <p className="cg-review-link-note">
          This order has no live review link. Issuing one here keeps the client on this order —
          running the client-link wizard again would create a second order instead.
        </p>
      ) : (
        <p className="cg-review-link-url">{url}</p>
      )}

      <div className="cg-review-link-actions">
        {live === null ? null : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              navigator.clipboard
                .writeText(url)
                .then(() => setCopied(true))
                .catch(() => onError('Could not copy. Select the link above and copy it.'));
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        )}
        <Button
          // Primary only when it is the obvious move — no link, or one nearly
          // out. Never disabled otherwise: somebody may have a good reason to
          // replace a healthy link, and being wrong costs a button that looks
          // slightly too keen.
          variant={shouldReissue(life) ? 'primary' : 'secondary'}
          size="sm"
          disabled={reissue.isPending}
          onClick={() => {
            setCopied(false);
            reissue.mutate(
              { orderId, days: DEFAULT_REVIEW_LINK_DAYS },
              { onError: (err) => onError(cgErrorMessage(err.message)) },
            );
          }}
        >
          {reissue.isPending ? 'Issuing…' : live === null ? 'Issue a link' : 'Issue a new link'}
        </Button>
        {live === null || revoking ? null : (
          <Button variant="secondary" size="sm" onClick={() => setRevoking(true)}>
            Revoke…
          </Button>
        )}
      </div>

      {/* Two steps, and a mandatory reason. Revoking is the only action here a
          client can notice without being told, so six weeks later "why can't
          they open it" needs an answer in the order's own record — and the
          second step is what makes this read as a decision. */}
      {!revoking ? null : (
        <div className="cg-review-link-revoke">
          <Textarea
            label="Why is this link being revoked?"
            rows={2}
            placeholder="e.g. sent to the wrong contact at the client"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="cg-review-link-actions">
            <Button
              variant="danger"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => {
                revoke.mutate(
                  { orderId, reason },
                  {
                    onSuccess: () => {
                      setRevoking(false);
                      setReason('');
                    },
                    onError: (err) => onError(cgErrorMessage(err.message)),
                  },
                );
              }}
            >
              {revoke.isPending ? 'Revoking…' : 'Revoke link'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setRevoking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which factory this order goes to, and how to say so.
 *
 * The link is stored on supplier_handoffs, not on cg_orders — that is where
 * supplier_org_for_order and every supplier-facing function already read it,
 * and a column on the order would be a second truth to keep in step. Linking
 * here reuses an unconnected handoff for the chosen factory where one exists
 * and creates a minimal one where none does.
 *
 * Changing an already-linked supplier stays available deliberately: a PO
 * pointed at the wrong factory has to be fixable, and every change is written
 * to the order's history with both names.
 */
/**
 * The factory's pushback on this PO, and the way to close it.
 *
 * Until 20261020000100 this note existed in the database and was rendered on
 * exactly one screen — the supplier's own, showing them their own sentence.
 * Nobody at TAP could see it. An order where the factory had said "this cannot
 * be built as written" was, on every TAP surface, indistinguishable from one
 * the factory simply had not opened yet.
 *
 * Rendered above the form deliberately. The answer to a revision request is
 * almost always to change one of the fields below it — the target date, the
 * quantity — so the question belongs where somebody reads it before editing,
 * not in a panel further down that they reach after they have already decided
 * what to change.
 */
function SupplierRevisionPanel({ order }: { order: CgOrder }) {
  const acknowledge = useAcknowledgeSupplierRevision();
  const [error, setError] = useState('');
  const summary = revisionSummary(order);

  if (summary === '') {
    return null;
  }
  const outstanding = revisionOutstanding(order);

  return (
    <div className={outstanding ? 'cg-revision-panel is-open' : 'cg-revision-panel'}>
      <div className="cg-revision-head">
        <Badge tone={outstanding ? 'danger' : 'neutral'}>
          {outstanding ? 'Supplier blocked' : 'Handled'}
        </Badge>
        <span>{summary}</span>
      </div>
      {/* The factory's own words, verbatim. Unlike the client's decision note
          — which the supplier portal deliberately withholds — this one is
          written BY the factory TO us, so there is nobody to protect from it
          and every reason to show it exactly as sent. */}
      <blockquote className="cg-revision-note">{order.supplier_revision_note}</blockquote>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {!outstanding ? null : (
        <Button
          variant="secondary"
          disabled={acknowledge.isPending}
          onClick={() => {
            setError('');
            acknowledge.mutate(order.id, {
              onError: (err) => setError(cgErrorMessage(err.message)),
            });
          }}
        >
          {acknowledge.isPending ? 'Saving…' : 'Mark handled'}
        </Button>
      )}
    </div>
  );
}

const EMPTY_ITEM: ItemDraft = {
  productType: '',
  sku: '',
  spec: '',
  description: '',
  units: '',
  unitPrice: '',
  unitCost: '',
};

function draftsToInputs(drafts: ItemDraft[], fallbackType: string): CgItemInput[] {
  return drafts
    .filter((draft) => draft.units.trim() !== '' || draft.description.trim() !== '')
    .map((draft) => ({
      productType: draft.productType === '' ? fallbackType : draft.productType,
      description: draft.description,
      units: Math.max(1, Math.round(Number(draft.units) || 1)),
      unitPriceUsd: Number(draft.unitPrice) || 0,
      unitCostUsd: draft.unitCost.trim() === '' ? null : Number(draft.unitCost) || 0,
      sku: draft.sku,
      spec: draft.spec,
    }));
}

function CgNewOrder(props: { onCreated: () => void; prefill: OrderPrefill | null }) {
  const workspaceSampleDays =
    useWorkspaceSettings().data?.default_sample_due_days ?? DEFAULT_SAMPLE_DUE_DAYS;
  const create = useCreateCgOrder();
  const setHandoffPo = useSetHandoffPo();
  /**
   * Which kind of order this is, asked FIRST.
   *
   * Kyu: "the type decision happens at the handoff, not after the wrong
   * workflow has started." Everything below follows from it — which stages the
   * status list offers, and which fields are on screen at all.
   *
   * Arriving from a supplier handoff means a factory is already involved, so
   * that path is custom and the chooser is not offered.
   */
  const fromHandoff = props.prefill !== null;
  const [orderType, setOrderType] = useState<CgOrderType>('custom');
  const statuses = useCgStatuses(orderType);
  const productTypes = useCgProductTypes();
  const orderNoRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [client, setClient] = useState(props.prefill?.client ?? '');
  const [region, setRegion] = useState('US');
  // Deliberately not a fixed string. The two ladders share no stage ids, so a
  // status left over from the other pipeline would be refused by the database
  // on submit — and the first stage of whichever ladder is loaded is always
  // the right default.
  const [status, setStatus] = useState('');
  const firstStage = (statuses.data ?? [])[0]?.id ?? '';
  const effectiveStatus = status === '' ? firstStage : status;
  const [shippingHandling, setShippingHandling] = useState('');
  const [shipTo, setShipTo] = useState('');
  const [targetShipDate, setTargetShipDate] = useState('');
  // undefined until somebody types: the workspace default is not known on the
  // first render, and seeding the string there would pin 21 forever.
  const [sampleDueDays, setSampleDueDays] = useState<string | undefined>(undefined);
  const effectiveSampleDays = sampleDueDays ?? String(workspaceSampleDays);
  const [costOverride, setCostOverride] = useState('');
  const [datePaid, setDatePaid] = useState('');
  const [datePoSent, setDatePoSent] = useState(props.prefill?.datePoSent ?? '');
  const [dateAcked, setDateAcked] = useState(props.prefill?.dateAcknowledged ?? '');
  const [dateShipped, setDateShipped] = useState('');
  const [notes, setNotes] = useState('');
  const [notesToMfr, setNotesToMfr] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([
    {
      ...EMPTY_ITEM,
      description: props.prefill?.description ?? '',
      units: props.prefill?.units ?? '',
    },
  ]);

  const types = productTypes.data ?? [];
  const fallbackType = types[0]?.id ?? 'Other';

  // The cursor goes to the one field the flow cannot know. Everything else is
  // either filled or safely blank, so the first keystroke should be the
  // Shopify order id being pasted.
  useEffect(() => {
    orderNoRef.current?.focus();
  }, []);

  // The handoff's product type is free text and the picker's options arrive
  // asynchronously, so the match runs once the list lands rather than at
  // first render. Only ever fills a still-empty picker — never overwrites a
  // choice somebody made while the query was in flight.
  const prefillType = props.prefill?.productType ?? '';
  useEffect(() => {
    if (prefillType === '' || types.length === 0) {
      return;
    }
    const matched = matchProductType(
      prefillType,
      types.map((type) => type.id),
    );
    if (matched === '') {
      return;
    }
    setItems((current) =>
      current.map((item, index) =>
        index === 0 && item.productType === '' ? { ...item, productType: matched } : item,
      ),
    );
  }, [prefillType, types]);

  const submit = () => {
    setError('');
    if (orderNo.trim() === '' || client.trim() === '') {
      setError('Order ID and Client are required.');
      return;
    }
    const input: CgOrderInput = {
      orderNo,
      client,
      region,
      orderType,
      status: effectiveStatus,
      shippingHandlingUsd: Number(shippingHandling) || 0,
      shipTo,
      targetShipDate: targetShipDate === '' ? null : targetShipDate,
      // Never 0 or negative: the column has a check constraint, and a blank
      // field should fall back to the standard rather than be refused.
      sampleDueDays: Math.max(1, Math.round(Number(effectiveSampleDays) || workspaceSampleDays)),
      costOverrideUsd: costOverride.trim() === '' ? null : Number(costOverride) || 0,
      datePaid: datePaid === '' ? null : datePaid,
      datePoSent: datePoSent === '' ? null : datePoSent,
      dateAcknowledged: dateAcked === '' ? null : dateAcked,
      dateShipped: dateShipped === '' ? null : dateShipped,
      // A brand-new order has not shipped, so there is nothing to track yet.
      trackingNumber: null,
      notes,
      notesToManufacturer: notesToMfr,
      items: draftsToInputs(items, fallbackType),
    };
    create.mutate(input, {
      onSuccess: (order) => {
        // The link is stamped only after the order exists, and a failure here
        // does not undo the order: the order is the valuable thing, and an
        // unstamped handoff still offers "Generate PO" to link it by hand.
        const handoffId = props.prefill?.handoffId;
        if (handoffId !== undefined && handoffId !== '') {
          setHandoffPo.mutate({ handoffId, orderId: order.id });
        }
        props.onCreated();
      },
      onError: (err) => setError(cgErrorMessage(err.message)),
    });
  };

  return (
    <div className="cg-form">
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {/* The type decision comes before the form, not after it. A custom order
          and a stock order share a client and a number and almost nothing
          else, and starting the wrong one means retyping all of it. */}
      {fromHandoff ? null : (
        <fieldset className="cg-order-type">
          <legend>What kind of order is this?</legend>
          <div className="cg-order-type-options">
            <label className="cg-order-type-option">
              <input
                type="radio"
                name="cg-order-type"
                value="custom"
                checked={orderType === 'custom'}
                onChange={() => {
                  setOrderType('custom');
                  setStatus('');
                }}
              />
              <span>
                <strong>Custom CG Order</strong>
                <span className="cg-muted">
                  Made to order: approvals, samples, a PO and a supplier.
                </span>
              </span>
            </label>
            <label className="cg-order-type-option">
              <input
                type="radio"
                name="cg-order-type"
                value="in_stock"
                checked={orderType === 'in_stock'}
                onChange={() => {
                  setOrderType('in_stock');
                  setStatus('');
                }}
              />
              <span>
                <strong>In-Stock Bracelet Order</strong>
                <span className="cg-muted">
                  From warehouse stock: invoice, payment, fulfil, ship.
                </span>
              </span>
            </label>
          </div>
          {orderType === 'in_stock' ? (
            <p className="cg-muted cg-order-type-note">
              This order cannot reach Fulfillment or Shipped until its Date Paid is set.
            </p>
          ) : null}
        </fieldset>
      )}

      <div className="cg-form-grid">
        <Input
          ref={orderNoRef}
          label="Order ID *"
          value={orderNo}
          onChange={(e) => setOrderNo(e.target.value)}
          placeholder="#1039"
        />
        <Input label="Client *" value={client} onChange={(e) => setClient(e.target.value)} />
        <Select
          label="Region"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          options={[
            { value: 'US', label: 'US' },
            { value: 'International', label: 'International' },
          ]}
        />
        <Select
          label="Status"
          value={effectiveStatus}
          onChange={(e) => setStatus(e.target.value)}
          options={(statuses.data ?? []).map((entry) => ({
            value: entry.id,
            label: statusLabelOf(entry),
          }))}
        />
        <Input
          label="Date Paid"
          type="date"
          value={datePaid}
          onChange={(e) => setDatePaid(e.target.value)}
        />
        {/* Supplier fields. An In-Stock order has no factory, so a PO date
            and an acknowledgement date are boxes nobody can fill — and a
            blank field somebody thinks they ought to fill is worse than one
            that was never offered. */}
        {hasSupplierStage(orderType) ? (
          <>
            <Input
              label="Date PO Sent to ES"
              type="date"
              value={datePoSent}
              onChange={(e) => setDatePoSent(e.target.value)}
            />
            <Input
              label="Date Acknowledged"
              type="date"
              value={dateAcked}
              onChange={(e) => setDateAcked(e.target.value)}
            />
          </>
        ) : null}
        <Input
          label="Date Shipped"
          type="date"
          value={dateShipped}
          onChange={(e) => setDateShipped(e.target.value)}
        />
        <Input
          label="Shipping & Handling ($)"
          value={shippingHandling}
          onChange={(e) => setShippingHandling(e.target.value)}
        />
        <Input
          label="Target Ship Date"
          type="date"
          value={targetShipDate}
          onChange={(e) => setTargetShipDate(e.target.value)}
        />
        {hasSupplierStage(orderType) ? (
          <Input
            label="Sample SLA (Days)"
            type="number"
            min={1}
            value={effectiveSampleDays}
            onChange={(e) => setSampleDueDays(e.target.value)}
            hint={`Days the supplier gets for samples once they confirm. This workspace's default is ${String(workspaceSampleDays)}.`}
          />
        ) : null}
        <Input
          label="Total Order Cost ($) — override"
          value={costOverride}
          onChange={(e) => setCostOverride(e.target.value)}
          placeholder="Leave blank to derive from line items"
        />
      </div>
      <ItemsEditor items={items} onChange={setItems} productTypes={types.map((t) => t.id)} />
      {/* One free-text block rather than split address columns, matching the
          column's own design note. It prints as the SHIP TO panel on the
          Production PO, so line breaks here are the line breaks a factory
          reads. */}
      <Textarea
        label="Ship to"
        value={shipTo}
        onChange={(e) => setShipTo(e.target.value)}
        rows={3}
        placeholder={'Acme Fulfilment\n1550 NW 1st Ave\nBoca Raton, FL 33432'}
      />
      {/* Printed on the Production PO. There is no PO on the In-Stock path. */}
      {hasSupplierStage(orderType) ? (
        <Textarea
          label="Notes to manufacturer"
          value={notesToMfr}
          onChange={(e) => setNotesToMfr(e.target.value)}
          placeholder="Build instructions the factory reads. Printed on the PO."
        />
      ) : null}
      <Textarea
        label="Internal notes (not sent to the supplier)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="cg-form-actions">
        <Button onClick={submit} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create order'}
        </Button>
      </div>
    </div>
  );
}

function ItemsEditor(props: {
  items: ItemDraft[];
  onChange: (items: ItemDraft[]) => void;
  productTypes: string[];
}) {
  const patch = (index: number, change: Partial<ItemDraft>) => {
    props.onChange(props.items.map((item, at) => (at === index ? { ...item, ...change } : item)));
  };
  // Add had no undo. A row opened to see what it looked like stayed on screen
  // with no way to take it back, and a half-filled one saves: draftsToInputs
  // keeps any row with a description OR units, defaulting the missing units to
  // 1 — so "Beaded Bracelet" with an empty Units box reaches the factory as an
  // order for one bracelet. Removing the row is the only honest way out of it.
  const remove = (index: number) => {
    props.onChange(props.items.filter((_item, at) => at !== index));
  };
  return (
    <fieldset className="cg-items-editor">
      <legend>Line items (revenue &amp; supplier cost, USD)</legend>
      {props.items.map((item, index) => (
        <div key={index} className="cg-item-row">
          <Select
            label="Product type"
            value={item.productType}
            onChange={(e) => patch(index, { productType: e.target.value })}
            options={[
              { value: '', label: '— select —' },
              ...props.productTypes.map((type) => ({ value: type, label: type })),
            ]}
          />
          <Input
            label="Description"
            value={item.description}
            onChange={(e) => patch(index, { description: e.target.value })}
          />
          <Input
            label="SKU"
            value={item.sku}
            onChange={(e) => patch(index, { sku: e.target.value })}
            placeholder="4oBlankCharm"
          />
          <Input
            label="Spec"
            value={item.spec}
            onChange={(e) => patch(index, { spec: e.target.value })}
            placeholder="8mm, matte black cord"
          />
          <Input
            label="Units"
            value={item.units}
            onChange={(e) => patch(index, { units: e.target.value })}
          />
          <Input
            label="Unit price ($)"
            value={item.unitPrice}
            onChange={(e) => patch(index, { unitPrice: e.target.value })}
          />
          <Input
            label="Unit supplier cost ($)"
            value={item.unitCost}
            onChange={(e) => patch(index, { unitCost: e.target.value })}
            placeholder="Blank until vendor report arrives"
          />
          {/* Never on the only row: an order with no line items sends the
              factory a PO with an empty item table. */}
          {props.items.length === 1 ? null : (
            <div className="cg-item-remove">
              <Button variant="ghost" size="sm" onClick={() => remove(index)}>
                Remove line
              </Button>
            </div>
          )}
        </div>
      ))}
      <Button
        variant="secondary"
        onClick={() => props.onChange([...props.items, { ...EMPTY_ITEM }])}
      >
        Add line item
      </Button>
    </fieldset>
  );
}

function CgOrderDialog(props: {
  title: string;
  initial: CgOrder;
  initialItems: CgOrderItem[];
  onClose: () => void;
  onRequestDelete: () => void;
}) {
  const update = useUpdateCgOrder();
  // The order's OWN ladder. A quick edit must not be able to offer a stage
  // from the other pipeline, which the database would refuse on save.
  const dialogType: CgOrderType = props.initial.order_type === 'in_stock' ? 'in_stock' : 'custom';
  const statuses = useCgStatuses(dialogType);
  const productTypes = useCgProductTypes();
  const people = useTapPeople(true);
  // Only a fallback here. An order being edited already has a stored SLA and
  // must keep it: re-seeding an existing deadline from a workspace default
  // somebody changed last week would move a clock a factory is running to.
  const workspaceSampleDays =
    useWorkspaceSettings().data?.default_sample_due_days ?? DEFAULT_SAMPLE_DUE_DAYS;
  const reviewToken = useOrderReviewToken(props.initial.id);
  const gateTasks = useGateTasks(props.initial.id);
  const gateState = useGateState(props.initial.id);
  const sendPo = useSendPoToSupplier();
  const orderSupplier = useOrderSupplier(props.initial.id);
  const clientApproved = useClientApproved(props.initial.id);
  const poDocuments = usePoDocuments(props.initial.id);
  const certificate = useCertificate(props.initial.id);
  const overrideGate = useOverrideFinancialGate();
  const { access } = useAuth();
  const isAdmin =
    access.status === 'approved' &&
    (access.profile.internal_role === 'tap_admin' || access.profile.is_owner);
  const [error, setError] = useState('');
  const [client, setClient] = useState(props.initial.client);
  const [region, setRegion] = useState(props.initial.region);
  const [status, setStatus] = useState(props.initial.status);
  const [shippingHandling, setShippingHandling] = useState(
    String(props.initial.shipping_handling_usd),
  );
  const [shipTo, setShipTo] = useState(props.initial.ship_to);
  const [targetShipDate, setTargetShipDate] = useState(props.initial.target_ship_date ?? '');
  const [sampleDueDays, setSampleDueDays] = useState(String(props.initial.sample_due_days));
  const [costOverride, setCostOverride] = useState(
    props.initial.cost_override_usd === null ? '' : String(props.initial.cost_override_usd),
  );
  // No setters: both are stamped by the checklist's actions and refused on a
  // direct write, so the drawer reads them and never edits them.
  const datePaid = props.initial.date_paid ?? '';
  const datePoSent = props.initial.date_po_sent ?? '';
  const [dateAcked, setDateAcked] = useState(props.initial.date_acknowledged ?? '');
  const [dateShipped, setDateShipped] = useState(props.initial.date_shipped ?? '');
  const [tracking, setTracking] = useState(props.initial.tracking_number ?? '');
  const [copied, setCopied] = useState(false);
  const [ownerId, setOwnerId] = useState(props.initial.owner_id ?? '');
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [noticeCopied, setNoticeCopied] = useState(false);
  const [notes, setNotes] = useState(props.initial.notes);
  const [notesToMfr, setNotesToMfr] = useState(props.initial.notes_to_manufacturer);
  const [items, setItems] = useState<ItemDraft[]>(
    props.initialItems.length === 0
      ? [{ ...EMPTY_ITEM }]
      : props.initialItems.map((item) => ({
          productType: item.product_type,
          sku: item.sku,
          spec: item.spec,
          description: item.description,
          units: String(item.units),
          unitPrice: String(item.unit_price_usd),
          unitCost: item.unit_cost_usd === null ? '' : String(item.unit_cost_usd),
        })),
  );
  const types = productTypes.data ?? [];
  const gateList = gateTasks.data ?? [];
  const gateSteps = gateState.data ?? [];
  // Three states, not two. `undefined` means "we do not know yet" — still
  // loading, or the lookup failed — and only a definite null becomes the
  // blocking sentence. Collapsing unknown into "not linked" would put a red
  // warning on every order for the moment the drawer opens, and on every order
  // if the RPC ever errors. The database refuses a PO with no supplier either
  // way, so being optimistic here cannot let a bad one through.
  const supplierLinked =
    orderSupplier.isPending || orderSupplier.data === undefined
      ? undefined
      : orderSupplier.data !== null;
  const approved = clientApproved.isPending ? undefined : clientApproved.data;
  const poProblem = sendPoProblem(props.initial.status, gateSteps, supplierLinked, approved);
  // Both gates now ask about the MOVE rather than about the current status, so
  // each needs the ranked rows rather than a list of ids. See financialGate.ts:
  // with two boundaries in the run-up to the PO, a status change that jumps
  // over one of them would otherwise slip past the rule guarding it.
  const statusList = statuses.data ?? [];
  const gateShut = gateBlocks(props.initial.status, gateList, statusList);
  const gateOutstanding = outstandingTasks(gateList);
  const gateBlocked = blockedByGate(props.initial.status, gateList, statusList);
  const approvalBlocked = blockedByApproval(props.initial.status, approved, statusList);

  const ownerChoices = (people.data ?? []).map((person) => ({
    id: person.id,
    label: person.display_name.trim() !== '' ? person.display_name : person.email,
  }));

  // The revised proof is uploaded elsewhere; what this offers is the sentence
  // and the link, which is the part people get wrong.
  const liveToken = reviewToken.data ?? null;
  const noticeText =
    liveToken === null
      ? ''
      : revisionNotice({
          clientName: client,
          // No component picker here: the order-level notice covers "a revised
          // proof" without naming one. Naming the wrong component is worse
          // than naming none.
          componentName: '',
          orderNo: props.initial.order_no,
          reviewUrl: reviewUrl(window.location.origin, liveToken.token),
        });

  const save = () => {
    setError('');
    update.mutate(
      {
        orderId: props.initial.id,
        update: {
          client,
          region,
          status,
          shippingHandlingUsd: Number(shippingHandling) || 0,
          shipTo,
          targetShipDate: targetShipDate === '' ? null : targetShipDate,
          sampleDueDays: Math.max(1, Math.round(Number(sampleDueDays) || workspaceSampleDays)),
          costOverrideUsd: costOverride.trim() === '' ? null : Number(costOverride) || 0,
          // datePaid and datePoSent are deliberately absent: the database
          // refuses a browser write to either, and sending the unchanged value
          // would only work by accident.
          dateAcknowledged: dateAcked === '' ? null : dateAcked,
          dateShipped: dateShipped === '' ? null : dateShipped,
          trackingNumber: tracking.trim() === '' ? null : tracking.trim(),
          // '' is the Unassigned option and means "nobody owns this", which is
          // a real choice — cg_order_owner() then falls back rather than the
          // order becoming unnotifiable. Sending undefined would leave the
          // previous owner in place and make unassigning impossible.
          ownerId: ownerId === '' ? null : ownerId,
          notes,
          notesToManufacturer: notesToMfr,
        },
        items: draftsToInputs(items, types[0]?.id ?? 'Other'),
      },
      { onSuccess: props.onClose, onError: (err) => setError(cgErrorMessage(err.message)) },
    );
  };

  return (
    <Drawer
      open
      title={props.title}
      onClose={props.onClose}
      menuItems={[{ label: 'Delete order', danger: true, onSelect: props.onRequestDelete }]}
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      {/* ── At the TOP, not the footer ───────────────────────────────────
          This link used to live in the drawer's footer, under a form long
          enough that reaching it meant scrolling past line items, ship-to,
          notes to manufacturer and internal notes. The way out of the
          drawer was hidden at the bottom of the drawer.

          It is the first thing in the panel now, because "I actually need
          the whole job" is a realisation you have on arrival, not after
          reading everything. */}
      <Link className="cg-open-case-file" to={`/cg-orders/${props.initial.id}`}>
        Open the full job page →
      </Link>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <SupplierRevisionPanel order={props.initial} />
      <div className="cg-form-grid">
        <Input label="Client" value={client} onChange={(e) => setClient(e.target.value)} />
        <Select
          label="Region"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          options={[
            { value: 'US', label: 'US' },
            { value: 'International', label: 'International' },
          ]}
        />
        <Select
          label="Status"
          value={status}
          hint={
            // The gate's reason wins when it applies: it is the one somebody
            // can act on, and showing both would leave them guessing which is
            // actually stopping them.
            gateShut
              ? FINANCIAL_GATE_MESSAGE
              : forwardOnlyReason(props.initial, statuses.data ?? [])
          }
          onChange={(e) => setStatus(e.target.value)}
          options={(statuses.data ?? []).map((entry) => ({
            value: entry.id,
            label: statusLabelOf(entry),
            disabled:
              blockedStatuses(props.initial, statuses.data ?? []).has(entry.id) ||
              gateBlocked.has(entry.id) ||
              approvalBlocked.has(entry.id),
          }))}
        />
        <Select
          label="Owner"
          value={ownerId}
          hint={ownerHint(
            resolveOwner(
              { ...props.initial, owner_id: ownerId === '' ? null : ownerId },
              ownerChoices,
            ),
          )}
          onChange={(e) => setOwnerId(e.target.value)}
          options={[
            // Unassigned is offered deliberately. Forcing an owner on every
            // order would mean somebody picking a name to clear the field,
            // which is worse than the honest fallback the hint describes.
            { value: '', label: 'Unassigned' },
            ...ownerChoices.map((person) => ({ value: person.id, label: person.label })),
          ]}
        />
        {/* Both of these are stamped by the two actions in the checklist
            above, and refused on a direct write by
            cg_orders_guard_automated_dates. readOnly rather than disabled: a
            disabled input is skipped by screen readers and greys the value out,
            and the value is the thing somebody came here to read. */}
        <Input
          label="Date Paid"
          type="date"
          value={datePaid}
          readOnly
          hint="Set by Mark Invoice Paid."
        />
        <Input
          label="Date PO Sent to ES"
          type="date"
          value={datePoSent}
          readOnly
          hint="Set when the PO goes to El Salvador."
        />
        <Input
          label="Date Acknowledged"
          type="date"
          value={dateAcked}
          onChange={(e) => setDateAcked(e.target.value)}
        />
        <Input
          label="Date Shipped"
          type="date"
          value={dateShipped}
          onChange={(e) => setDateShipped(e.target.value)}
        />
        <div className="cg-tracking-field">
          <Input
            label="Tracking number"
            value={tracking}
            placeholder="Paste the courier's number"
            onChange={(e) => {
              setTracking(e.target.value);
              setCopied(false);
            }}
          />
          {tracking.trim() === '' ? null : (
            <div className="cg-tracking-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Clipboard access is refused in some contexts; saying so
                  // beats a button that silently does nothing.
                  navigator.clipboard
                    .writeText(tracking.trim())
                    .then(() => setCopied(true))
                    .catch(() => setError('Could not copy. Select the number and copy it.'));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
              {(() => {
                const match = detectCarrier(tracking);
                // No link when the carrier is a guess too far: sending someone
                // to the wrong courier is worse than making them paste it.
                return match === null ? (
                  <span className="deal-note-empty">Carrier not recognised</span>
                ) : (
                  <a
                    className="cg-tracking-link"
                    href={match.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Track with {match.carrier}
                  </a>
                );
              })()}
            </div>
          )}
        </div>
        <Input
          label="Shipping & Handling ($)"
          value={shippingHandling}
          onChange={(e) => setShippingHandling(e.target.value)}
        />
        <Input
          label="Target Ship Date"
          type="date"
          value={targetShipDate}
          onChange={(e) => setTargetShipDate(e.target.value)}
        />
        <Input
          label="Sample SLA (Days)"
          type="number"
          min={1}
          value={sampleDueDays}
          onChange={(e) => setSampleDueDays(e.target.value)}
          hint={`Days the supplier gets for samples once they confirm. This workspace's default is ${String(workspaceSampleDays)}.`}
        />
        <Input
          label="Total Order Cost ($) — override"
          value={costOverride}
          onChange={(e) => setCostOverride(e.target.value)}
        />
      </div>
      <ItemsEditor items={items} onChange={setItems} productTypes={types.map((t) => t.id)} />
      {/* One free-text block rather than split address columns, matching the
          column's own design note. It prints as the SHIP TO panel on the
          Production PO, so line breaks here are the line breaks a factory
          reads. */}
      <Textarea
        label="Ship to"
        value={shipTo}
        onChange={(e) => setShipTo(e.target.value)}
        rows={3}
        placeholder={'Acme Fulfilment\n1550 NW 1st Ave\nBoca Raton, FL 33432'}
      />
      {/* Printed on the Production PO. There is no PO on the In-Stock path. */}
      {hasSupplierStage(dialogType) ? (
        <Textarea
          label="Notes to manufacturer"
          value={notesToMfr}
          onChange={(e) => setNotesToMfr(e.target.value)}
          placeholder="Build instructions the factory reads. Printed on the PO."
        />
      ) : null}
      <Textarea
        label="Internal notes (not sent to the supplier)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      {/* ── The financial gate ──
          Shown before anybody presses anything. The database refuses the
          promotion either way (cg_orders_guard_financial_gate); what this adds
          is WHICH work is outstanding, which the refusal itself cannot say. */}
      {/* ── The financial gate checklist ──
          Two steps and the action each one unlocks, on the order itself. Both
          write the same tasks rows the gate trigger reads, through RPCs, so
          somebody who cannot see the auto-created project the tasks live in
          can still do the work. */}
      {/* Shown from 'Order Placed' onward rather than only on the gated
          status. The Shopify order and the invoice are often in hand before
          the client signs, and a checklist that appeared only afterwards hid
          the half of the gate somebody can get ahead on.

          The panel itself moved to FinancialGatePanel.tsx when the job page
          became the way people open an order — see that file. One checklist,
          two surfaces; two would be two places to fix the day the gate
          changes. */}
      {props.initial.status !== APPROVAL_STATUS && props.initial.status !== GATED_STATUS ? null : (
        <div>
          <FinancialGatePanel order={props.initial} onError={setError} />

          {/* Sent or not sent, and it says which.
              The action used to succeed in silence: the status dropdown
              changed and nothing else did, so "it worked" and "nothing
              happened" looked identical and the honest response was to click
              again. The database refuses a second send — the row is locked and
              the status is no longer 'Order Placed' — but being protected from
              a mistake is not the same as being told you did not make one.

              Read from date_po_sent rather than from a flag set on success, so
              the confirmation is still there tomorrow, on a reload, for
              somebody who did not press the button. */}
          {props.initial.date_po_sent === null ? (
            <div className="cg-gate-send">
              <Button
                disabled={poProblem !== null || sendPo.isPending}
                onClick={() =>
                  sendPo.mutate(props.initial.id, {
                    onSuccess: () => setStatus('PO Sent to ES'),
                    onError: (err) => setError(cgErrorMessage(err.message)),
                  })
                }
              >
                {sendPo.isPending ? 'Sending…' : 'Send PO to Supplier (El Salvador)'}
              </Button>
              {/* Never a disabled button on its own. The whole reason this batch
                  exists is that people met a refusal with no way to act on it. */}
              <p className="cg-gate-send-note">
                {poProblem ??
                  'Attaches the client-approved proofs, stamps the PO date and moves the order to PO Sent to ES.'}
              </p>
            </div>
          ) : (
            <p className="cg-gate-sent">
              <span className="cg-gate-sent-mark" aria-hidden="true">
                ✓
              </span>
              PO sent {props.initial.date_po_sent}
              {orderSupplier.data?.organizationName === undefined
                ? ''
                : ` to ${orderSupplier.data.organizationName}`}
              . The issued sheet is under Purchase orders below.
            </p>
          )}
        </div>
      )}

      {!gateShut ? null : (
        <div className="cg-gate-banner" role="status">
          <p className="cg-gate-message">{FINANCIAL_GATE_MESSAGE}</p>
          <ul className="cg-gate-list">
            {gateOutstanding.map((entry) => (
              <li key={entry.id}>{entry.title}</li>
            ))}
          </ul>
          {!isAdmin ? null : overrideOpen ? (
            <div className="cg-gate-override">
              <Select
                label="Advance to"
                value={overrideStatus}
                onChange={(e) => setOverrideStatus(e.target.value)}
                options={[
                  { value: '', label: 'Choose a status' },
                  ...[...gateBlocked].map((entry) => ({ value: entry, label: entry })),
                ]}
              />
              <Textarea
                label="Reason for the override"
                value={overrideReason}
                rows={2}
                placeholder="e.g. Wire cleared Friday, receipt filed in Drive."
                onChange={(e) => setOverrideReason(e.target.value)}
              />
              <div className="cg-gate-override-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setOverrideOpen(false);
                    setOverrideReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={overrideGate.isPending || overrideStatus === ''}
                  onClick={() => {
                    const problem = overrideProblem(overrideReason, isAdmin);
                    if (problem !== null) {
                      setError(problem);
                      return;
                    }
                    setError('');
                    overrideGate.mutate(
                      {
                        orderId: props.initial.id,
                        status: overrideStatus,
                        reason: overrideReason.trim(),
                      },
                      {
                        onSuccess: () => {
                          setOverrideOpen(false);
                          setOverrideReason('');
                          setStatus(overrideStatus);
                        },
                        onError: (err) => setError(cgErrorMessage(err.message)),
                      },
                    );
                  }}
                >
                  {overrideGate.isPending ? 'Overriding…' : 'Override and advance'}
                </Button>
              </div>
            </div>
          ) : (
            /* Deliberately not a plain button that just does it. The reason is
               mandatory in the database, so a one-click override would only
               ever produce an error — and the second step is what makes this
               feel like a decision rather than a shortcut. */
            <Button variant="secondary" size="sm" onClick={() => setOverrideOpen(true)}>
              Admin override…
            </Button>
          )}
        </div>
      )}

      {/* What the client has actually done.
          The first screen in the app to read order_assets and asset_decisions
          at all. Before this, a rejection existed only in the client's portal
          and in a notification that named neither the component nor the note,
          so the person who had to fix the artwork could not see what was wrong
          with it — or that anything was. */}
      <ClientApprovalPanel
        orderId={props.initial.id}
        closed={isArchivedStatus(props.initial.status, statusList)}
      />

      {/* The link itself, before the sentence that carries it. Nothing in the
          app could re-issue or revoke one until 20261023000100 — an expired
          link's only recovery was a duplicate order. */}
      <ReviewLinkPanel orderId={props.initial.id} live={liveToken} onError={setError} />

      {/* The revision notice.
          Shown only when the order has a LIVE review link — an expired or
          revoked token pasted to a client is worse than no link, because it
          looks like TAP sent something broken. The message itself is built in
          revisionNotice.ts so its wording has a test. */}
      {noticeText === '' ? null : (
        <div className="cg-revision-notice">
          <p className="cg-revision-notice-label">Revised proof uploaded?</p>
          <p className="cg-revision-notice-preview">{noticeText}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              navigator.clipboard
                .writeText(noticeText)
                .then(() => setNoticeCopied(true))
                .catch(() =>
                  // Clipboard access is refused in some contexts. The message
                  // is already on screen above, so say what to do instead of
                  // leaving a button that silently does nothing.
                  setError('Could not copy. Select the message above and copy it.'),
                );
            }}
          >
            {noticeCopied ? 'Copied' : 'Copy revision notice'}
          </Button>
        </div>
      )}

      {/* The paper trail. Every PO issued for this job, current and
          superseded, opened and printed from the frozen copy rather than
          regenerated from whatever the order says today. */}
      {/* The certificate stays on the card forever — delivered, closed or
          otherwise. Backtracking a finished job has to find the evidence of
          what the client approved, not a record that it once existed. */}
      <div className="cg-po-history">
        <p className="cg-activity-title">Client approval</p>
        <CertificateLink certificate={certificate.data} />
      </div>

      <div className="cg-po-history">
        <p className="cg-activity-title">Purchase orders</p>
        <PoHistory
          documents={poDocuments.data ?? []}
          isPending={poDocuments.isPending}
          isError={poDocuments.isError}
        />
      </div>

      {/* Last, because it is the answer to a question rather than a thing to
          do. Everything above is an action; this is why the order looks the
          way it does. */}
      <OrderActivityFeed orderId={props.initial.id} people={ownerChoices} />
    </Drawer>
  );
}

const REPORT_PERIODS = [
  'All time',
  'Today',
  'This week',
  'This month',
  'Last month',
  'This quarter',
  // Kyu, 22 Sep: "what's our profit from ... Q2". Once a quarter closes it is
  // one click; any older quarter is a custom range.
  'Last quarter',
  'This year',
  'Last 7 days',
  'Last 30 days',
  'Last 90 days',
  'Custom range…',
] as const;

type ReportPeriod = (typeof REPORT_PERIODS)[number];

const DATE_BASES = ['Date Paid', 'Date PO Sent', 'Date Shipped', 'Date Created'] as const;
type DateBasis = (typeof DATE_BASES)[number];

const GROUP_BYS = [
  'None',
  'Client',
  'Pipeline',
  'Region',
  'Product Type',
  'Status',
  'Month',
] as const;
type GroupBy = (typeof GROUP_BYS)[number];

interface ReportColumn {
  key: string;
  label: string;
  value: (order: CgOrder, items: CgOrderItem[], bill: CgOrderCost[]) => string | number;
}

const ESSENTIAL_COLUMNS = [
  'orderId',
  'client',
  'status',
  'total',
  'cost',
  'margin',
  'daysToShip',
] as const;

/**
 * Whether the current selection is exactly this preset, so the pill can show
 * it. Exact match rather than superset: ticking one extra column means the
 * report is no longer "Financials", and claiming otherwise would be a lie the
 * user cannot see through.
 */
export function isPresetActive(checked: Set<string>, keys: readonly string[]): boolean {
  return checked.size === keys.length && keys.every((key) => checked.has(key));
}

/** One-click column sets for the report table (redesign spec: presets). */
const COLUMN_PRESETS: { label: string; keys: readonly string[] }[] = [
  { label: 'Essentials', keys: ESSENTIAL_COLUMNS },
  {
    label: 'Financials',
    keys: [
      'orderId',
      'client',
      'amount',
      'shipping_handling',
      'total',
      'cost',
      'margin',
      'marginPct',
    ],
  },
  {
    label: 'Logistics',
    keys: [
      'orderId',
      'client',
      'status',
      'datePoSent',
      'dateAcked',
      'dateShipped',
      'daysToShip',
      'daysToAck',
      'daysInProduction',
    ],
  },
  {
    label: 'Everything',
    keys: [
      'orderId',
      'client',
      'pipeline',
      'region',
      'productType',
      'units',
      'amount',
      'shipping_handling',
      'total',
      'cost',
      'margin',
      'marginPct',
      'status',
      'datePaid',
      'datePoSent',
      'dateAcked',
      'dateShipped',
      'daysToShip',
      'daysToAck',
      'daysInProduction',
      'notes',
    ],
  },
];

function reportColumns(): ReportColumn[] {
  return [
    { key: 'orderId', label: 'Order ID', value: (o) => o.order_no },
    { key: 'client', label: 'Client', value: (o) => o.client },
    // Named in words, like the Orders table's badge. A report read next
    // quarter has no switcher above it to say which pipeline it covered.
    { key: 'pipeline', label: 'Pipeline', value: (o) => orderTypeLabel(o) },
    { key: 'region', label: 'Region', value: (o) => o.region },
    {
      key: 'productType',
      label: 'Product Type',
      value: (_o, i) => [...new Set(i.map((x) => x.product_type))].join('; '),
    },
    { key: 'units', label: 'Units', value: (_o, i) => i.reduce((s, x) => s + x.units, 0) },
    { key: 'amount', label: 'Revenue $', value: (_o, i) => orderRevenue(i) },
    {
      key: 'shipping_handling',
      label: 'Shipping & Handling $',
      value: (o) => Number(o.shipping_handling_usd),
    },
    { key: 'total', label: 'Order Total $', value: (o, i) => orderTotal(o, i) },
    // Since 20270162000100 these follow the supplier bill, the same lens the
    // Orders list and the order page use. The keys stay, so a saved template
    // that picked "cost" still finds its column.
    { key: 'cost', label: 'Supplier Bill $', value: (o, i, b) => orderProfit(o, i, b).bill ?? '' },
    { key: 'margin', label: 'Profit $', value: (o, i, b) => orderProfit(o, i, b).profit ?? '' },
    {
      key: 'marginPct',
      label: 'Margin %',
      value: (o, i, b) => {
        const pct = orderProfit(o, i, b).marginPct;
        return pct === null ? '' : pct.toFixed(1);
      },
    },
    { key: 'status', label: 'Status', value: (o) => o.status },
    { key: 'datePaid', label: 'Date Paid', value: (o) => o.date_paid ?? '' },
    { key: 'datePoSent', label: 'Date PO Sent', value: (o) => o.date_po_sent ?? '' },
    { key: 'dateAcked', label: 'Date Acked', value: (o) => o.date_acknowledged ?? '' },
    { key: 'dateShipped', label: 'Date Shipped', value: (o) => o.date_shipped ?? '' },
    { key: 'daysToShip', label: 'Days to Ship', value: (o) => daysToShip(o) ?? '' },
    { key: 'daysToAck', label: 'Days to Acknowledge', value: (o) => daysToAcknowledge(o) ?? '' },
    {
      key: 'daysInProduction',
      label: 'Days in Production',
      value: (o) => daysInProduction(o) ?? '',
    },
    { key: 'notes', label: 'Notes', value: (o) => o.notes },
  ];
}

export function periodRange(
  period: ReportPeriod,
  todayStr: string,
  from: string,
  to: string,
): [string, string] {
  const today = new Date(`${todayStr}T00:00:00Z`);
  const iso = (date: Date) => date.toISOString().slice(0, 10);
  const startOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  switch (period) {
    case 'All time':
      return ['0000-01-01', '9999-12-31'];
    case 'Today':
      return [todayStr, todayStr];
    case 'This week': {
      const day = (today.getUTCDay() + 6) % 7;
      const monday = new Date(today.getTime() - day * 86_400_000);
      return [iso(monday), todayStr];
    }
    case 'This month':
      return [iso(startOfMonth), todayStr];
    case 'Last month': {
      const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
      const end = new Date(startOfMonth.getTime() - 86_400_000);
      return [iso(start), iso(end)];
    }
    case 'This quarter': {
      const quarterMonth = Math.floor(today.getUTCMonth() / 3) * 3;
      return [iso(new Date(Date.UTC(today.getUTCFullYear(), quarterMonth, 1))), todayStr];
    }
    case 'Last quarter': {
      const quarterMonth = Math.floor(today.getUTCMonth() / 3) * 3;
      const thisQuarter = new Date(Date.UTC(today.getUTCFullYear(), quarterMonth, 1));
      const start = new Date(Date.UTC(today.getUTCFullYear(), quarterMonth - 3, 1));
      return [iso(start), iso(new Date(thisQuarter.getTime() - 86_400_000))];
    }
    case 'This year':
      return [`${today.getUTCFullYear()}-01-01`, todayStr];
    case 'Last 7 days':
      return [iso(new Date(today.getTime() - 6 * 86_400_000)), todayStr];
    case 'Last 30 days':
      return [iso(new Date(today.getTime() - 29 * 86_400_000)), todayStr];
    case 'Last 90 days':
      return [iso(new Date(today.getTime() - 89 * 86_400_000)), todayStr];
    case 'Custom range…':
      return [from === '' ? '0000-01-01' : from, to === '' ? '9999-12-31' : to];
  }
}

function basisDate(order: CgOrder, basis: DateBasis): string | null {
  switch (basis) {
    case 'Date Paid':
      return order.date_paid;
    case 'Date PO Sent':
      return order.date_po_sent;
    case 'Date Shipped':
      return order.date_shipped;
    case 'Date Created':
      return order.created_at.slice(0, 10);
  }
}

function CgReports({ scope }: { scope: CgPipelineScope }) {
  const { orders, items, itemsByOrder, billsByOrder } = useOrderData();
  // Under All this loads both ladders, so the Status filter can name a stage
  // from either one. Filtering to a stage the list cannot show would leave a
  // report that silently returns nothing.
  const statuses = useCgStatuses(scope);
  const productTypes = useCgProductTypes();
  const templates = useCgTemplates();
  const saveTemplate = useSaveCgTemplate();
  const removeTemplate = useDeleteCgTemplate();

  const columns = useMemo(reportColumns, []);
  // Opens on a window that actually holds orders. 'Last 30 days' looked like a
  // sensible default and wasn't: CG volume is a handful of orders a quarter, so
  // a month-wide window on Date Paid showed "0 orders" every single time the
  // page opened, and a report that always reads empty stops being opened.
  const [period, setPeriod] = useState<ReportPeriod>('This year');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [basis, setBasis] = useState<DateBasis>('Date Paid');
  const [groupBy, setGroupBy] = useState<GroupBy>('None');
  const [statusFilter, setStatusFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [clientContains, setClientContains] = useState('');
  const [checked, setChecked] = useState<Set<string>>(() => new Set(ESSENTIAL_COLUMNS));
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  if (orders.isLoading || items.isLoading) {
    return <Skeleton lines={8} />;
  }
  const today = todayIso();
  const [rangeFrom, rangeTo] = periodRange(period, today, from, to);
  // The pipeline first, before every other filter. Kyu asked for exactly this:
  // "Reporting stays unified with a filter: All / Custom Only / In-Stock Only."
  const inScope = filterOrdersByScope(orders.data ?? [], scope);
  const filtered = inScope.filter((order) => {
    const at = basisDate(order, basis);
    if (at === null || at < rangeFrom || at > rangeTo) {
      return false;
    }
    if (statusFilter !== '' && order.status !== statusFilter) {
      return false;
    }
    if (regionFilter !== '' && order.region !== regionFilter) {
      return false;
    }
    if (productFilter !== '') {
      const orderItems = itemsByOrder.get(order.id) ?? [];
      if (!orderItems.some((item) => item.product_type === productFilter)) {
        return false;
      }
    }
    if (
      clientContains.trim() !== '' &&
      !order.client.toLowerCase().includes(clientContains.trim().toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  /**
   * Under All, the Pipeline column is not optional.
   *
   * Same rule as the Orders table, and the same reason Kyu gave for it: with
   * both pipelines in one list, a row that does not say which one it is blurs
   * into the other. On a downloaded report that is worse, because the switcher
   * that would have explained it is not in the file.
   */
  /**
   * A stage filter left over from the other pipeline.
   *
   * Applying a saved template, or switching pipelines with a stage chosen,
   * leaves a filter naming a stage the current ladder does not have. The
   * filter STAYS applied — the report has to mean what the controls say — but
   * the answer is then zero rows for a reason nobody could work out from the
   * screen, so the screen says it.
   */
  const loadedStatuses = statuses.data ?? [];
  const strandedStatus =
    statusFilter !== '' && !loadedStatuses.some((entry) => entry.id === statusFilter)
      ? statusFilter
      : '';

  const activeColumns = columns.filter(
    (column) => checked.has(column.key) || (scope === 'all' && column.key === 'pipeline'),
  );
  const groupKey = (order: CgOrder): string => {
    switch (groupBy) {
      case 'None':
        return '';
      case 'Client':
        return order.client;
      case 'Pipeline':
        return orderTypeLabel(order);
      case 'Region':
        return order.region;
      case 'Status':
        return order.status;
      case 'Month':
        return (basisDate(order, basis) ?? '').slice(0, 7);
      case 'Product Type': {
        const orderItems = itemsByOrder.get(order.id) ?? [];
        return [...new Set(orderItems.map((item) => item.product_type))].join('; ') || '(none)';
      }
    }
  };
  const groups = new Map<string, CgOrder[]>();
  for (const order of filtered) {
    const key = groupKey(order);
    const list = groups.get(key);
    if (list === undefined) {
      groups.set(key, [order]);
    } else {
      list.push(order);
    }
  }

  const matrix: (string | number)[][] = [];
  const header = activeColumns.map((column) => column.label);
  for (const [key, groupOrders] of [...groups.entries()].sort()) {
    if (groupBy !== 'None') {
      matrix.push([`${groupBy}: ${key === '' ? '(none)' : key} (${groupOrders.length})`]);
    }
    for (const order of groupOrders) {
      matrix.push(
        activeColumns.map((column) =>
          column.value(order, itemsByOrder.get(order.id) ?? [], billsByOrder.get(order.id) ?? []),
        ),
      );
    }
  }

  const csvText = () =>
    [header, ...matrix]
      .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(','))
      .join('\n');

  const download = (name: string, mime: string, content: string) => {
    const blob = new Blob([content], { type: mime });
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = name;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  // The pipeline is in the FILE NAME, not just the rows. A CSV of custom-only
  // orders called cg-report-20260826.csv is indistinguishable from one holding
  // everything, and it will outlive the screen that made it.
  const stamp = `${scopeFileTag(scope)}-${today.replaceAll('-', '')}`;
  const currentConfig = (): Json => ({
    period,
    from,
    to,
    basis,
    groupBy,
    statusFilter,
    regionFilter,
    productFilter,
    clientContains,
    columns: [...checked],
  });
  const applyConfig = (config: Json) => {
    const c = config as Record<string, unknown>;
    if (typeof c.period === 'string' && (REPORT_PERIODS as readonly string[]).includes(c.period)) {
      setPeriod(c.period as ReportPeriod);
    }
    if (typeof c.from === 'string') setFrom(c.from);
    if (typeof c.to === 'string') setTo(c.to);
    if (typeof c.basis === 'string' && (DATE_BASES as readonly string[]).includes(c.basis)) {
      setBasis(c.basis as DateBasis);
    }
    if (typeof c.groupBy === 'string' && (GROUP_BYS as readonly string[]).includes(c.groupBy)) {
      setGroupBy(c.groupBy as GroupBy);
    }
    if (typeof c.statusFilter === 'string') setStatusFilter(c.statusFilter);
    if (typeof c.regionFilter === 'string') setRegionFilter(c.regionFilter);
    if (typeof c.productFilter === 'string') setProductFilter(c.productFilter);
    if (typeof c.clientContains === 'string') setClientContains(c.clientContains);
    if (Array.isArray(c.columns)) {
      setChecked(new Set(c.columns.filter((value): value is string => typeof value === 'string')));
    }
  };

  return (
    <div>
      {/* Above the order report, not below it. "How fast is this factory" is
          the question somebody opens Reports to answer before they start
          filtering rows, and it needs no configuration to be useful. */}
      <section className="cg-scorecard-section">
        <h3 className="project-category-heading">Supplier timings</h3>
        <p className="cg-scorecard-lede">
          Average days per handover, over every job with recorded stages. Our own review time is on
          here too.
        </p>
        <SupplierScorecard />
      </section>

      <div className="cg-form-grid">
        <Select
          label="Time Period"
          value={period}
          onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
          options={REPORT_PERIODS.map((entry) => ({ value: entry, label: entry }))}
        />
        {period === 'Custom range…' ? (
          <>
            <Input
              label="From"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </>
        ) : null}
        <Select
          label="Date Basis"
          value={basis}
          onChange={(e) => setBasis(e.target.value as DateBasis)}
          options={DATE_BASES.map((entry) => ({ value: entry, label: entry }))}
        />
        <Select
          label="Group By"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as GroupBy)}
          options={GROUP_BYS.map((entry) => ({ value: entry, label: entry }))}
        />
        <Select
          label="Status Filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All statuses' },
            // The stranded value first, kept in the list. A select whose own
            // value is not among its options renders blank in some browsers,
            // which reads as "the filter is off" while it is very much on.
            ...(strandedStatus === ''
              ? []
              : [{ value: strandedStatus, label: `${strandedStatus} — other pipeline` }]),
            ...loadedStatuses.map((entry) => ({
              value: entry.id,
              label: statusLabelOf(entry),
            })),
          ]}
        />
        <Select
          label="Region Filter"
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          options={[
            { value: '', label: 'All regions' },
            { value: 'US', label: 'US' },
            { value: 'International', label: 'International' },
          ]}
        />
        <Select
          label="Product Filter"
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          options={[
            { value: '', label: 'All products' },
            ...(productTypes.data ?? []).map((entry) => ({ value: entry.id, label: entry.id })),
          ]}
        />
        <Input
          label="Client Contains"
          value={clientContains}
          onChange={(e) => setClientContains(e.target.value)}
        />
      </div>
      <div className="cg-columns-bar">
        <button
          type="button"
          className="cg-sla-trigger"
          aria-expanded={columnsOpen}
          onClick={() => setColumnsOpen((prev) => !prev)}
        >
          Columns ({checked.size})
        </button>
      </div>
      {columnsOpen ? (
        <fieldset className="cg-column-picker filter-popover">
          <legend>Columns to Include</legend>
          {/* Presets live inside the picker rather than out on the bar. Out
              there their only feedback was the count in the trigger, at the
              far end of the row — and when the report matches no orders there
              is no table either, so a preset click looked like a dead button.
              In here the checkboxes tick under the cursor. */}
          <div className="cg-column-presets">
            {COLUMN_PRESETS.map((preset) => {
              const active = isPresetActive(checked, preset.keys);
              return (
                <button
                  key={preset.label}
                  type="button"
                  className={`cg-column-preset${active ? ' cg-column-preset--active' : ''}`}
                  aria-pressed={active}
                  onClick={() => setChecked(new Set(preset.keys))}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          {columns.map((column) => (
            <label key={column.key} className="cg-column-option">
              <input
                type="checkbox"
                checked={checked.has(column.key)}
                onChange={(event) => {
                  const next = new Set(checked);
                  if (event.target.checked) {
                    next.add(column.key);
                  } else {
                    next.delete(column.key);
                  }
                  setChecked(next);
                }}
              />
              {column.label}
            </label>
          ))}
        </fieldset>
      ) : null}
      <div className="cg-form-actions">
        <Button
          variant="secondary"
          onClick={() => download(`cg-report-${stamp}.csv`, 'text/csv', csvText())}
        >
          Download CSV
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            download(
              `cg-report-${stamp}.xls`,
              'application/vnd.ms-excel',
              `<table><tr>${header.map((h) => `<th>${h}</th>`).join('')}</tr>${matrix
                .map((row) => `<tr>${row.map((cell) => `<td>${String(cell)}</td>`).join('')}</tr>`)
                .join('')}</table>`,
            )
          }
        >
          Download Excel
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            document.body.classList.add('cg-printing');
            window.addEventListener(
              'afterprint',
              () => document.body.classList.remove('cg-printing'),
              { once: true },
            );
            window.print();
          }}
        >
          Print / PDF
        </Button>
      </div>
      <div className="cg-form-grid">
        <Select
          label="Saved templates"
          value=""
          onChange={(event) => {
            const found = (templates.data ?? []).find((entry) => entry.id === event.target.value);
            if (found !== undefined) {
              applyConfig(found.config);
            }
          }}
          options={[
            { value: '', label: '— select template —' },
            ...(templates.data ?? []).map((entry) => ({ value: entry.id, label: entry.name })),
          ]}
        />
        <Input
          label="Save current as"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        <div className="cg-form-actions">
          <Button
            variant="secondary"
            disabled={templateName.trim() === '' || saveTemplate.isPending}
            onClick={() => {
              saveTemplate.mutate({ name: templateName, config: currentConfig() });
              setTemplateName('');
            }}
          >
            Save template
          </Button>
          {(templates.data ?? []).length === 0 ? null : (
            <Select
              label="Delete a template"
              value=""
              onChange={(event) => {
                if (event.target.value !== '') {
                  removeTemplate.mutate(event.target.value);
                }
              }}
              options={[
                { value: '', label: '— delete template —' },
                ...(templates.data ?? []).map((entry) => ({ value: entry.id, label: entry.name })),
              ]}
            />
          )}
        </div>
      </div>
      {/* Right under the time picker, so choosing "Last quarter" answers the
          profit question before the detailed table. Same orders as the table:
          it reads the report's own filtered list. */}
      <ProfitReport summary={profitSummary(filtered, itemsByOrder, billsByOrder)} period={period} />
      <section className="tracker-dashboard-card cg-report-print">
        <h2 className="tracker-dashboard-card-title">
          Report · {filtered.length} orders <Badge tone="brand">{period}</Badge>
        </h2>
        {strandedStatus === '' ? null : (
          <InlineError>
            {`The stage filter "${strandedStatus}" belongs to the other pipeline, so nothing here can match it. Clear the filter or switch pipelines.`}
          </InlineError>
        )}
        {matrix.length === 0 ? (
          <p className="cg-muted">No orders match this report.</p>
        ) : (
          <table className="tracker-table">
            <thead>
              <tr>
                {header.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((row, at) => (
                <tr key={at}>
                  {row.length === 1 && groupBy !== 'None' ? (
                    <td colSpan={header.length}>
                      <strong>{row[0]}</strong>
                    </td>
                  ) : (
                    row.map((cell, cellAt) => <td key={cellAt}>{String(cell)}</td>)
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
